import { Alert, Box, Card, CardContent, CardHeader, Grid, Theme, Typography } from '@mui/material';
import { EarnEventPayload, EventEnveloppe, EventName, Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  GetProfileIncludeOptions,
  ProfileAttributes,
} from '@loyalty/shared-common/profile-api-client';
import { QlfPlaceholder, colorPalette, sxs } from '@qualifio/ui-commons';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import { faFileCircleInfo } from '@fortawesome/pro-solid-svg-icons/faFileCircleInfo';
import { faSquareInfo } from '@fortawesome/pro-solid-svg-icons/faSquareInfo';
import { faSquarePollVertical } from '@fortawesome/pro-solid-svg-icons/faSquarePollVertical';
import { faUserCircle } from '@fortawesome/pro-solid-svg-icons/faUserCircle';
import { faUserSecret } from '@fortawesome/pro-solid-svg-icons/faUserSecret';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomLink } from '../../../shared/CustomLink';
import { DetailForm } from '../../../shared/layout/DetailForm';
import { EventReportDetailsList } from './EventReportDetailsList';
import { Loader } from '../../../shared/layout/Loader';
import { QlfPlaceholderIcon } from '../../../shared/qlf-placeholder-icon/QlfPlaceholderIcon';
import { SxStyles } from '../../../shared/utils/types';
import { eventReportsIcon } from '../../../shared/icons';
import { isInShell } from '../../../shell/shell';
import { useAccounts } from '../../../accounts/AccountsContext';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useEventReports } from '../../EventReportsContext';
import { useFeatures } from '../../../features/FeaturesContext';
import { usePrograms } from '../../../loyalty/ProgramsContext';
import { useSelectiveEffect } from '../../../shared/use-selective-effect';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  mainIcon: {
    color: '#BBBBBB',
    marginBottom: spacing(1),
  },
  list: {
    margin: 0,
    padding: 0,
  },
  form: {
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'hidden',
  },
}));

const styles: SxStyles = {
  card: {
    backgroundColor: colorPalette.grey.light,
    borderTop: ({ blocked }) =>
      `4px solid ${blocked ? colorPalette.red.main : colorPalette.blue.main}`,
    marginBottom: 2,
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  cardHeader: {
    height: 66,
    borderBottom: `1px solid ${colorPalette.grey.main}`,
    padding: [2, 3],
  },
  cardTitle: {
    display: 'inline',
    margin: [0, 1],
    textAlign: 'left',
    color: colorPalette.marine.main,
    '&.svg': {
      color: colorPalette.marine.main,
    },
  },
  cardContent: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    paddingX: 3,
    paddingY: 1,
    maxWidth: '100%',
    minWidth: 0,
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    paddingX: 1,
    paddingY: 1,
  },
  property: {
    flex: '0 0 40%',
    fontWeight: '600',
    minWidth: 0,
  },
  value: {
    flex: '1 1 60%',
    marginLeft: '5px',
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  jsonDump: {
    display: 'block',
    m: 0,
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'auto',
    whiteSpace: 'pre',
    width: '100%',
  },
  title: {
    fontSize: 20,
    marginBottom: 3,
    textAlign: 'left',
    '&:after': {
      backgroundColor: colorPalette.primary.main,
      content: "''",
      display: 'block',
      height: 2,
      marginTop: 1,
      marginX: 0,
      marginBottom: 3,
      width: 40,
    },
  },
  profileError: {
    marginTop: '20px',
    textAlign: 'center',
    fontWeight: 600,
  },
};

const warningIcon = (
  <FontAwesomeIcon
    icon={faCircleExclamation}
    color={colorPalette.red.main}
    style={{ marginRight: '5px' }}
  />
);

const eventNamesWithoutPayload = [
  EventName.PROFILE_CREATION,
  EventName.BIRTHDAY,
  EventName.MEMBER_VISIT,
  EventName.MEMBER_CLICK,
  EventName.LEVEL_CHANGE,
];

export function EventReportDetail() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { selectedReport } = useEventReports();
  const { programs } = usePrograms();
  const { currentAccount } = useAccounts();
  const program = selectedReport?.programId
    ? programs.find(program => program.id === selectedReport?.programId) || null
    : undefined;
  const { features } = useFeatures();
  const { advancedView } = features[Feature.EVENT_REPORT];
  const { profileApi } = useApi();
  const [profile, setProfile] = useState<ProfileAttributes>();
  const result = selectedReport?.result;

  const isMemberOfProgram =
    !!program &&
    !!profile &&
    profile.memberships?.some(({ programId }) => programId === program.id);
  const profileId = selectedReport?.result?.analytics?.profileId ?? profile?.id;

  useEffect(() => {
    if (!currentAccount || !selectedReport?.profile) {
      return;
    }

    const getProfilePromise =
      selectedReport.profile.kind === 'id'
        ? profileApi.getProfile(selectedReport.profile.value, {
            params: {
              include: [GetProfileIncludeOptions.memberships],
            },
          })
        : profileApi.lookupProfileByIdentity(
            currentAccount.id,
            {
              kind: selectedReport.profile.kind,
              value: selectedReport.profile.value,
            },
            {
              params: {
                include: [GetProfileIncludeOptions.memberships],
              },
            },
          );

    getProfilePromise.then(setProfile).catch(() => setProfile(undefined));
  }, [selectedReport, currentAccount, profileApi]);

  const crmLink = `${window.parent.location.origin}${
    isInShell() ? '/s/loyalty' : '/loy'
  }/accounts/${currentAccount?.id}/programs/${program?.id}/profiles?search=${profileId}`;

  const profileCard = (
    <Card sx={sxs(styles.card)}>
      <CardHeader
        sx={styles.cardHeader}
        titleTypographyProps={{
          variant: 'h3',
        }}
        title={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              {selectedReport?.isTestUser ? (
                <FontAwesomeIcon icon={faUserSecret} color={colorPalette.marine.main} />
              ) : (
                <FontAwesomeIcon icon={faUserCircle} color={colorPalette.marine.main} />
              )}
              <Box sx={styles.cardTitle}>{t('event.reports.profile.title')}</Box>
            </div>
            {currentAccount && program && isMemberOfProgram && profileId ? (
              <CustomLink to={crmLink} target="_blank" rel="noreferrer">
                {t('event.reports.profile.link')}
              </CustomLink>
            ) : !profileId ? (
              <div>
                {warningIcon}
                {t('event.reports.profile.not.found')}
              </div>
            ) : program === null ? (
              <div>
                {warningIcon}
                {t('event.reports.program.not.found')}
              </div>
            ) : program ? (
              <div>
                {warningIcon}
                {t('event.reports.profile.not.member')}
              </div>
            ) : (
              // program === undefined <=> there was no programId in the event
              <></>
            )}
          </Box>
        }
      />
      <CardContent
        sx={sxs(styles.cardContent, {
          overflow: 'auto',
          height: '70%',
        })}
      >
        <ul className={classes.list}>
          <Box component="li" sx={styles.listItem}>
            <Typography variant="body1" component="div" sx={styles.property}>
              {t('event.report.profile.properties.identity.kind')}
            </Typography>
            <Typography variant="body1" component="div" sx={styles.value}>
              {selectedReport?.profile?.kind ?? '-'}
            </Typography>
          </Box>
          <Box component="li" sx={styles.listItem}>
            <Typography variant="body1" component="div" sx={styles.property}>
              {t('event.report.profile.properties.identity.value')}
            </Typography>
            <Typography variant="body1" component="div" sx={styles.value}>
              {selectedReport?.profile?.value ?? '-'}
            </Typography>
          </Box>
          <Box component="li" sx={styles.listItem}>
            <Typography variant="body1" component="div" sx={styles.property}>
              {t('event.report.profile.properties.profile.id')}
            </Typography>
            <Typography variant="body1" component="div" sx={styles.value}>
              {selectedReport?.result?.analytics?.profileId ?? profile?.id ?? '-'}
            </Typography>
          </Box>
        </ul>
      </CardContent>
    </Card>
  );

  const generalInformationCard = selectedReport ? (
    <Card sx={sxs(styles.card)}>
      <CardHeader
        sx={styles.cardHeader}
        titleTypographyProps={{
          variant: 'h3',
        }}
        title={
          <div>
            <FontAwesomeIcon icon={faSquareInfo} color={colorPalette.marine.main} />
            <Box sx={styles.cardTitle}>{t('event.reports.generals.title')}</Box>
          </div>
        }
      />
      <CardContent
        sx={sxs(styles.cardContent, {
          overflow: 'auto',
          height: '80%',
        })}
      >
        <EventReportDetailsList eventReport={selectedReport} program={program} />
      </CardContent>
    </Card>
  ) : null;

  const resultCard = (
    <Card sx={sxs(styles.card)}>
      <CardHeader
        sx={styles.cardHeader}
        titleTypographyProps={{
          variant: 'h3',
        }}
        title={
          <div>
            <FontAwesomeIcon icon={faSquarePollVertical} color={colorPalette.marine.main} />
            <Box sx={styles.cardTitle}>{t('event.reports.result.title')}</Box>
          </div>
        }
      />
      <CardContent
        sx={sxs(styles.cardContent, {
          overflow: 'auto',
          height: '70%',
        })}
      >
        <Box component="pre" sx={styles.jsonDump}>
          {JSON.stringify(result, undefined, 2)}
        </Box>

        <Box sx={styles.profileError}>
          {!selectedReport?.result?.analytics?.profileId && !profile?.id
            ? t('event.report.profile.properties.no-profile')
            : ''}
        </Box>
      </CardContent>
    </Card>
  );

  const payloadCard = selectedReport ? (
    <Card sx={sxs(styles.card)}>
      <CardHeader
        sx={styles.cardHeader}
        titleTypographyProps={{
          variant: 'h3',
        }}
        title={
          <div>
            <FontAwesomeIcon icon={faFileCircleInfo} color={colorPalette.marine.main} />
            <Box sx={styles.cardTitle}>{t('event.reports.payload.title')}</Box>
          </div>
        }
      />
      <CardContent
        sx={sxs(styles.cardContent, {
          overflow: 'auto',
          height: '80%',
        })}
      >
        {!selectedReport?.eventName ||
        eventNamesWithoutPayload.includes(selectedReport.eventName as EventName) ? (
          t('event.reports.internal.no.payload')
        ) : advancedView ? (
          <Payload eventId={selectedReport.eventId} />
        ) : (
          t('event.reports.no.permission.payload')
        )}
      </CardContent>
    </Card>
  ) : null;

  return !selectedReport ? (
    <QlfPlaceholder
      icon={<QlfPlaceholderIcon icon={eventReportsIcon} className={classes.mainIcon} />}
      primaryText={t('event-reports.states.empty.title')}
      secondaryText={t('event-reports.states.empty.description')}
    />
  ) : (
    <DetailForm>
      <DetailForm.Form className={classes.form}>
        <Typography sx={styles.title} variant="h2">
          <Box sx={{ alignItems: 'center', display: 'flex' }}>
            <div>{t('event.reports.title')}</div>
          </Box>
        </Typography>

        <Grid container spacing={2} sx={{ gap: 'unset !important', minWidth: 0, width: '100%' }}>
          <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
            {profileCard}
            {generalInformationCard}
          </Grid>
          <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
            {resultCard}
            {payloadCard}
          </Grid>
        </Grid>
      </DetailForm.Form>
    </DetailForm>
  );
}

function Payload({ eventId }: { eventId: string }) {
  const { ingestorApi } = useApi();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>();
  const [event, setEvent] = useState<EventEnveloppe<EarnEventPayload>>();
  const { t } = useTranslation();

  useSelectiveEffect(
    () => {
      setError(undefined);
      ingestorApi
        .getEvent(eventId)
        .then(setEvent)
        .catch(setError)
        .finally(() => {
          setLoading(false);
        });
    },
    [ingestorApi, eventId],
    [eventId],
  );

  return loading ? (
    <Loader />
  ) : error ? (
    error?.response?.status === 404 ? (
      <Alert severity="warning">{t('event.reports.payload.content.deleted.message')}</Alert>
    ) : (
      <Alert color="error">{error.message}</Alert>
    )
  ) : (
    <Box component="pre" sx={styles.jsonDump}>
      <code>{JSON.stringify(event, null, 2)}</code>
    </Box>
  );
}
